import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { Observable, Subject } from 'rxjs';
import { CertificateManagementService } from '../../certificate-management/certificate-management.service';
import { Certificate } from '../../certificate-management/certificate-management.types';
import {
    CERTIFICATE_FILE_CONSTRAINTS,
    EmployeeCertificate,
} from '../employee-management.types';

@Component({
    selector: 'app-employee-certificates-manager',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        NzCardModule,
        NzButtonModule,
        NzListModule,
        NzDatePickerModule,
        NzSelectModule,
        NzSwitchModule,
        NzFormModule,
        NzTagModule,
    ],
    templateUrl: './employee-certificates-manager.component.html',
    styleUrl: './employee-certificates-manager.component.scss',
})
export class EmployeeCertificatesManagerComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() certificates: EmployeeCertificate[] = [];
    @Input() readonly: boolean = false;
    @Output() certificatesChange = new EventEmitter<EmployeeCertificate[]>();

    certificatesFormArray: FormArray;
    certificateTypes$: Observable<Certificate[]>;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _formBuilder: FormBuilder,
        private _certificateService: CertificateManagementService,
        private _messageService: NzMessageService
    ) {}

    ngOnInit(): void {
        // Initialize FormArray
        this.certificatesFormArray = this._formBuilder.array([]);

        // Load certificate types
        this._certificateService
            .getCertificateListWithoutPagination()
            .subscribe();
        this.certificateTypes$ = this._certificateService.allCertificates$;

        // If certificates provided, populate FormArray
        this.populateFromInput();
    }

    /**
     * Parents that receive data AFTER this component renders (e.g. the
     * register form's IC prefill, or its reset on an IC change) replace the
     * [certificates] array later. Rebuild the FormArray whenever the input
     * reference changes post-init; parents that set data before render are
     * unaffected (firstChange is handled by ngOnInit).
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes['certificates'] &&
            !changes['certificates'].firstChange &&
            this.certificatesFormArray
        ) {
            this.certificatesFormArray.clear();
            this.populateFromInput();
        }
    }

    private populateFromInput(): void {
        if (this.certificates && this.certificates.length > 0) {
            this.certificates.forEach((cert) => {
                this.certificatesFormArray.push(
                    this.createCertificateFormGroup(cert)
                );
            });
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private createCertificateFormGroup(cert?: EmployeeCertificate): FormGroup {
        // Determine if this is an existing certificate (has id and certificate_url)
        const isExistingCertificate = cert?.id && cert?.certificate_url;

        // Determine default has_expiry value
        const hasExpiry =
            cert?.has_expiry !== undefined ? cert.has_expiry : true;

        return this._formBuilder.group({
            // Existing certificate fields
            id: [cert?.id || ''],
            certificate_url: [cert?.certificate_url || ''],

            // Common fields
            certificate_type_id: [
                cert?.certificate_type_id || '',
                Validators.required,
            ],
            // File is only required for new certificates (not existing ones with URLs)
            file: [
                cert?.file || null,
                isExistingCertificate ? [] : Validators.required,
            ],
            filename: [cert?.filename || ''],
            has_expiry: [hasExpiry],
            // Expiry date is required when has_expiry is true
            expiry_date: [
                cert?.expiry_date || '',
                hasExpiry ? Validators.required : [],
            ],
        });
    }

    /**
     * Add a new certificate entry to the form array
     */
    addCertificate(): void {
        if (
            this.certificatesFormArray.length >=
            CERTIFICATE_FILE_CONSTRAINTS.maxCertificates
        ) {
            this._messageService.warning(
                `Maximum ${CERTIFICATE_FILE_CONSTRAINTS.maxCertificates} certificates allowed`
            );
            return;
        }

        const certificateForm = this.createCertificateFormGroup();
        this.certificatesFormArray.push(certificateForm);
        this.emitChanges();
    }

    /**
     * Remove a certificate entry from the form array
     */
    removeCertificate(index: number): void {
        this.certificatesFormArray.removeAt(index);
        this.emitChanges();
    }

    /**
     * Handle file selection from native input
     */
    handleFileSelect(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) {
            return;
        }

        // Validate file type
        const isValidType = CERTIFICATE_FILE_CONSTRAINTS.allowedTypes.includes(
            file.type
        );
        if (!isValidType) {
            this._messageService.error(
                'Only PDF, JPEG, JPG or PNG files are allowed'
            );
            input.value = ''; // Reset input
            return;
        }

        // Validate file size (5MB)
        const isValidSize =
            file.size <= CERTIFICATE_FILE_CONSTRAINTS.maxSizeBytes;
        if (!isValidSize) {
            this._messageService.error('File size should not exceed 5MB');
            input.value = ''; // Reset input
            return;
        }

        // Store file in FormGroup
        const certForm = this.getCertificateForm(index);
        certForm.patchValue({
            file: file,
            filename: file.name,
            // Clear certificate_url if user is replacing an existing certificate
            certificate_url: '',
        });
        certForm.get('file')?.markAsTouched();
        this.emitChanges();
    }

    /**
     * Handle expiry date change
     */
    onExpiryDateChange(date: Date, index: number): void {
        this.emitChanges();
    }

    /**
     * Handle certificate type change
     */
    onCertificateTypeChange(index: number): void {
        this.emitChanges();
    }

    /**
     * Handle expiry toggle change
     */
    onExpiryToggleChange(hasExpiry: boolean, index: number): void {
        const certForm = this.certificatesFormArray.at(index) as FormGroup;
        const expiryDateControl = certForm.get('expiry_date');

        if (!hasExpiry) {
            // Clear expiry date when toggle is disabled
            certForm.patchValue({ expiry_date: '' });
            expiryDateControl?.clearValidators();
            expiryDateControl?.markAsUntouched();
        } else {
            // Add required validator when toggle is enabled
            expiryDateControl?.setValidators(Validators.required);
            // Mark as touched if field is empty to show validation error immediately
            if (!expiryDateControl?.value) {
                expiryDateControl?.markAsTouched();
            }
        }

        expiryDateControl?.updateValueAndValidity();
        this.emitChanges();
    }

    /**
     * Disable past dates in date picker
     */
    disablePastDates = (current: Date): boolean => {
        return current && current < new Date(new Date().setHours(0, 0, 0, 0));
    };

    /**
     * Remove file from certificate
     */
    removeFile(index: number): void {
        const certForm = this.certificatesFormArray.at(index) as FormGroup;
        certForm.patchValue({
            file: null,
            filename: '',
        });
        this.emitChanges();
    }

    /**
     * Get certificate form group at index
     */
    getCertificateForm(index: number): FormGroup {
        return this.certificatesFormArray.at(index) as FormGroup;
    }

    /**
     * Check if certificate at index is an existing certificate (has certificate_url)
     */
    isExistingCertificate(index: number): boolean {
        const certForm = this.getCertificateForm(index);
        const certificateUrl = certForm.get('certificate_url')?.value;
        return !!certificateUrl;
    }

    private emitChanges(): void {
        const certificates = this.certificatesFormArray
            .value as EmployeeCertificate[];
        this.certificatesChange.emit(certificates);
    }
}
